import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1569541372853-97033ae8983b?fm=jpg&q=85&w=1600&auto=format&fit=crop";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
                    {/* Eyebrow */}
                    <div className="col-span-12 mb-4">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="flex items-center gap-4"
                        >
                            <span className="h-px w-12 bg-brand-orange" />
                            <span className="text-xs uppercase tracking-[0.3em] text-brand-orange font-medium">
                                Food &amp; Beverage Consultancy
                            </span>
                        </motion.div>
                    </div>

                    {/* Headline */}
                    <div className="col-span-12 lg:col-span-8">
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            data-testid="hero-headline"
                            className="font-serif text-[15vw] sm:text-[12vw] lg:text-[8.5vw] leading-[0.9] tracking-tighter text-brand-dark"
                        >
                            Drive more
                            <br />
                            <span className="italic font-light">revenue.</span>
                            <br />
                            Get more
                            <br />
                            <span className="text-brand-orange italic font-light">customers.</span>
                        </motion.h1>
                    </div>

                    {/* Image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="col-span-12 lg:col-span-4 lg:-mt-32"
                    >
                        <div className="relative aspect-[3/4] overflow-hidden">
                            <img
                                src={HERO_IMG}
                                alt="Warm, full-house restaurant interior with hanging pendant lights and guests dining — Eat + Drink Consulting"
                                width="1200"
                                height="1600"
                                loading="eager"
                                fetchpriority="high"
                                decoding="async"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/30 to-transparent" />
                        </div>
                    </motion.div>
                </div>

                {/* Subhead + CTA row */}
                <div className="grid grid-cols-12 gap-6 md:gap-10 mt-12 md:mt-20 pt-10 border-t border-brand-border-light">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="col-span-12 md:col-span-6 text-lg md:text-xl text-brand-muted font-light leading-relaxed max-w-xl"
                        data-testid="hero-subheading"
                    >
                        Strategic consulting for restaurants, bars, and hospitality brands.
                        Smart marketing, sharper operations, and revenue programs that actually move the needle.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        className="col-span-12 md:col-span-6 flex flex-col sm:flex-row md:justify-end items-start sm:items-center gap-4"
                    >
                        <a
                            href="#packages"
                            data-testid="hero-primary-cta"
                            className="inline-flex items-center gap-3 bg-brand-orange text-brand-cream px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-brand-orange-hover transition-colors group"
                        >
                            View Packages
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </a>
                        <a
                            href="#contact"
                            data-testid="hero-secondary-cta"
                            className="inline-flex items-center gap-3 border border-brand-dark text-brand-dark px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-brand-dark hover:text-brand-cream transition-colors"
                        >
                            Start a Conversation
                        </a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
